import {
  BookOpen,
  Check,
  ChevronRight,
  ExternalLink,
  EyeOff,
  Flame,
  Heart,
  Home,
  Instagram,
  KeyRound,
  LockKeyhole,
  Play,
  RotateCcw,
  Settings,
  Shuffle,
  SkipForward,
  Timer,
  X
} from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { cards, CARD_SOURCE } from "./data/cards.generated";
import {
  cardHeading,
  cardSubheading,
  cardTypeLabel,
  deckLabel,
  finalStatusLabel,
  levelLabel,
  modeLabel,
  renderPersonalizedText
} from "./lib/format";
import {
  choosePenetrationLevel,
  completeCurrentCard,
  dueModalKinds,
  getCurrentCard,
  isForeplayDeck,
  oppositeForeplayDeck,
  prepareNextCard,
  reshufflePenetrationCard,
  resumeSession,
  skipCurrentCard,
  startSession,
  summarizeSession,
  switchToDeck
} from "./lib/game";
import {
  ensureDeviceId,
  loadAppState,
  resetOnboarding,
  saveActiveSession,
  saveLicenseUnlock,
  saveOnboarding,
  storageAvailable
} from "./lib/storage";
import { accessLandingUrl, licenseErrorMessage, redeemLicenseKey, type LicenseRedeemResult } from "./lib/license";
import { SoundEffectPlayer } from "./lib/sfx";
import type { AppState, CardDeck, GameCard, GameMode, GameSession, ModalKind, PenetrationLevel, SessionSummary } from "./types";

type View = "license" | "onboarding" | "home" | "game" | "summary" | "settings" | "safe";
type QueueModal = "talks_checkpoint" | "foreplay_checkpoint" | "penetration_checkpoint" | "penetration_end";

const brandUrl = "https://www.instagram.com/the.lovebird.club";

function modeOptions(husbandName: string, wifeName: string): Array<{
  id: GameMode;
  title: string;
  description: string;
  icon: typeof Heart;
}> {
  const husband = husbandName || "Nama Suami";
  const wife = wifeName || "Nama Istri";
  return [
    {
      id: "talks",
      title: "Talks",
      description: "Kartu ngobrol random dan bergantian untuk membuka suasana.",
      icon: BookOpen
    },
    {
      id: "foreplay_husband",
      title: `Foreplay by ${husband}`,
      description: `${husband} menjadi performer dan menjalankan kartu secara berurutan.`,
      icon: Heart
    },
    {
      id: "foreplay_wife",
      title: `Foreplay by ${wife}`,
      description: `${wife} menjadi performer dan menjalankan kartu secara berurutan.`,
      icon: Heart
    },
    {
      id: "penetration",
      title: "Penetrasi",
      description: "Pilih level setiap ronde, lalu buka satu posisi random.",
      icon: Flame
    }
  ];
}

export function App() {
  const [state, setState] = useState<AppState>(() => loadAppState());
  const [view, setView] = useState<View>(() => initialView(loadAppState()));
  const [modal, setModal] = useState<ModalKind | undefined>();
  const [modalQueue, setModalQueue] = useState<QueueModal[]>([]);
  const [lastSummary, setLastSummary] = useState<SessionSummary | undefined>();
  const [storageOk, setStorageOk] = useState(true);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerRemaining, setTimerRemaining] = useState(0);
  const [cardRevealed, setCardRevealed] = useState(false);
  const sfx = useRef(new SoundEffectPlayer());

  const activeSession = state.activeSession;
  const currentCard = activeSession ? getCurrentCard(activeSession, cards) : undefined;

  useEffect(() => {
    setStorageOk(storageAvailable());
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    saveActiveSession(state.activeSession);
  }, [state.activeSession]);

  useEffect(() => {
    setCardRevealed(false);
    setTimerRunning(false);
    setTimerRemaining(currentCard?.durationSeconds ?? 0);
    if (view === "game" && currentCard?.id) {
      sfx.current.play("shuffle");
    }
  }, [currentCard?.id, currentCard?.durationSeconds]);

  useEffect(() => {
    if (!timerRunning || timerRemaining <= 0) return;
    const id = window.setInterval(() => {
      setTimerRemaining((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [timerRunning, timerRemaining]);

  useEffect(() => {
    if (timerRemaining === 0) setTimerRunning(false);
  }, [timerRemaining]);

  function updateState(updater: (current: AppState) => AppState) {
    setState(updater);
  }

  function setSession(session?: GameSession) {
    updateState((current) => ({ ...current, activeSession: session }));
  }

  function completeOnboarding(husbandName: string, wifeName: string) {
    const data = { onboardingCompleted: true, husbandName, wifeName };
    saveOnboarding(data);
    updateState((current) => ({ ...current, ...data }));
    setView("home");
  }

  async function redeemLicense(licenseKey: string): Promise<LicenseRedeemResult> {
    const deviceId = ensureDeviceId();
    const result = await redeemLicenseKey({ licenseKey, deviceId });
    if (!result.ok) return result;
    const unlock = {
      licenseKey: result.licenseKey,
      deviceId,
      unlockToken: result.unlockToken,
      activatedAt: result.activatedAt
    };
    saveLicenseUnlock(unlock);
    updateState((current) => ({ ...current, licenseUnlock: unlock }));
    setView(state.onboardingCompleted ? "home" : "onboarding");
    return result;
  }

  function beginMode(mode: GameMode) {
    sfx.current.play("start");
    setLastSummary(undefined);
    setSession(startSession(mode, cards));
    setView("game");
  }

  function continueSession() {
    if (!activeSession) return;
    sfx.current.play("start");
    setSession(resumeSession(activeSession, cards));
    setView("game");
  }

  function completeCard() {
    if (!activeSession || !currentCard) return;
    sfx.current.play("next");
    const completed = completeCurrentCard(activeSession, currentCard);
    const due = dueModalKinds(completed, currentCard);
    setSession(completed);
    if (due.length) {
      setModalQueue(due);
      setModal(due[0]);
      return;
    }
    advanceOrFinish(completed);
  }

  function skipCard() {
    if (!activeSession || !currentCard) return;
    sfx.current.play("next");
    setSession(skipCurrentCard(activeSession, currentCard, cards));
  }

  function changePenetrationCard() {
    if (!activeSession || !currentCard) return;
    sfx.current.play("next");
    setSession(reshufflePenetrationCard(activeSession, currentCard, cards));
  }

  function advanceOrFinish(session: GameSession) {
    const next = prepareNextCard(session, cards);
    if (next.status === "completed") {
      finishSession(next, "completed");
      return;
    }
    setSession(next);
  }

  function handleTalksCheckpoint(action: "continue" | "foreplay_husband" | "foreplay_wife" | "penetration") {
    if (!activeSession) return;
    clearQueueModal();
    if (action === "continue") {
      advanceOrFinish(activeSession);
      return;
    }
    setSession(switchToDeck(activeSession, action, cards));
  }

  function handleForeplayCheckpoint(action: "continue" | "switch" | "penetration") {
    if (!activeSession) return;
    clearQueueModal();
    if (action === "continue") {
      advanceOrFinish(activeSession);
      return;
    }
    if (action === "switch" && isForeplayDeck(activeSession.currentDeck)) {
      setSession(switchToDeck(activeSession, oppositeForeplayDeck(activeSession.currentDeck), cards));
      return;
    }
    if (action === "penetration") {
      setSession(switchToDeck(activeSession, "penetration", cards));
      return;
    }
  }

  function choosePenetration(level: PenetrationLevel) {
    if (!activeSession) return;
    sfx.current.play("select");
    setSession(choosePenetrationLevel(activeSession, level, cards));
  }

  function continuePenetrationCheckpoint() {
    if (!activeSession) return;
    clearQueueModal();
    advanceOrFinish(activeSession);
  }

  function handlePenetrationEnd(action: "home" | "restart") {
    clearQueueModal();
    if (action === "restart") {
      sfx.current.play("start");
      setLastSummary(undefined);
      setSession(startSession("penetration", cards));
      setTimerRunning(false);
      setView("game");
      return;
    }
    endSessionToHome();
  }

  function clearQueueModal() {
    setModalQueue([]);
    setModal(undefined);
  }

  function finishSession(session: GameSession, status: SessionSummary["finalStatus"]) {
    sfx.current.play("end");
    setLastSummary(summarizeSession(session, status));
    updateState((current) => ({ ...current, activeSession: undefined }));
    clearQueueModal();
    setTimerRunning(false);
    setView("home");
  }

  function endSessionToHome() {
    sfx.current.play("end");
    setLastSummary(undefined);
    updateState((current) => ({ ...current, activeSession: undefined }));
    clearQueueModal();
    setTimerRunning(false);
    setView("home");
  }

  if (view === "safe") {
    return <SafeScreen onBack={() => setView(activeSession ? "game" : "home")} />;
  }

  return (
    <main className={`app-shell view-${view}`}>
      {!storageOk && (
        <div className="storage-alert">
          Browser kamu membatasi penyimpanan. Sesi aktif mungkin tidak bisa dilanjutkan setelah browser ditutup.
        </div>
      )}

      {view === "license" && <LicenseGate onRedeem={redeemLicense} />}

      {view === "onboarding" && <Onboarding onComplete={completeOnboarding} />}

      {view === "home" && (
        <HomeScreen
          state={state}
          onStart={beginMode}
          onContinue={continueSession}
          onSettings={() => setView("settings")}
        />
      )}

      {view === "game" && activeSession && (
        <GameScreen
          session={activeSession}
          card={currentCard}
          husbandName={state.husbandName}
          wifeName={state.wifeName}
          cardRevealed={cardRevealed}
          timerRemaining={timerRemaining}
          timerRunning={timerRunning}
          onReveal={() => {
            sfx.current.play("select");
            setCardRevealed(true);
          }}
          onStartTimer={() => setTimerRunning(true)}
          onAddTime={() => setTimerRemaining((seconds) => seconds + 60)}
          onComplete={completeCard}
          onSkip={skipCard}
          onChangePenetrationCard={changePenetrationCard}
          onEnd={() => setModal("end_confirm")}
          onHide={() => setView("safe")}
          onBackHome={() => setView("home")}
          onChoosePenetrationLevel={choosePenetration}
          isBetweenCards={Boolean(modal || modalQueue.length)}
        />
      )}

      {view === "summary" && (
        <SummaryScreen summary={lastSummary} onHome={() => setView("home")} onNew={() => setView("home")} />
      )}

      {view === "settings" && (
        <SettingsScreen
          state={state}
          onBack={() => setView("home")}
          onSaveNames={(husbandName, wifeName) => {
            saveOnboarding({ onboardingCompleted: true, husbandName, wifeName });
            updateState((current) => ({ ...current, husbandName, wifeName }));
          }}
          onResetOnboarding={() => {
            resetOnboarding();
            updateState((current) => ({
              ...current,
              onboardingCompleted: false,
              husbandName: "",
              wifeName: ""
            }));
            setView("onboarding");
          }}
        />
      )}

      {modal && (
        <ModalLayer onClose={() => (modalQueue.length ? undefined : setModal(undefined))}>
          {modal === "talks_checkpoint" && (
            <TalksCheckpointModal
              husbandName={state.husbandName}
              wifeName={state.wifeName}
              onAction={handleTalksCheckpoint}
            />
          )}
          {modal === "foreplay_checkpoint" && (
            <ForeplayCheckpointModal
              currentDeck={activeSession?.currentDeck}
              husbandName={state.husbandName}
              wifeName={state.wifeName}
              onAction={handleForeplayCheckpoint}
            />
          )}
          {modal === "penetration_checkpoint" && <PenetrationCheckpointModal onContinue={continuePenetrationCheckpoint} />}
          {modal === "penetration_end" && <PenetrationEndModal onAction={handlePenetrationEnd} />}
          {modal === "end_confirm" && (
            <EndConfirmModal
              onCancel={() => setModal(undefined)}
              onEnd={() => activeSession && finishSession(activeSession, "ended")}
            />
          )}
        </ModalLayer>
      )}
    </main>
  );
}

function initialView(state: AppState): View {
  if (!state.licenseUnlock) return "license";
  return state.onboardingCompleted ? "home" : "onboarding";
}

function LicenseGate({ onRedeem }: { onRedeem: (licenseKey: string) => Promise<LicenseRedeemResult> }) {
  const [licenseKey, setLicenseKey] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const normalizedKey = licenseKey.trim();
  const canSubmit = normalizedKey.length >= 8 && !submitting;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setMessage("");
    const result = await onRedeem(normalizedKey);
    if (!result.ok) {
      setMessage(licenseErrorMessage(result.error));
    }
    setSubmitting(false);
  }

  return (
    <section className="license-screen" aria-labelledby="license-title">
      <div className="license-brand">
        <img className="license-logo" src="/img/logo-the-lovebird-club.png" alt="The Lovebird Club logo" />
        <p className="tagline">Play naughty. Get closer.</p>
        <h1 id="license-title">Intimacy Card Game</h1>
        <p>Konten privat untuk pasangan halal/sah.</p>
      </div>

      <form className="license-card" onSubmit={handleSubmit}>
        <div className="license-card-title">
          <LockKeyhole size={18} />
          <strong>Konten Terkunci</strong>
        </div>
        <p>Masukkan license key untuk membuka akses di device ini.</p>
        <label className="license-input">
          <KeyRound size={18} />
          <input
            value={licenseKey}
            onChange={(event) => setLicenseKey(event.target.value)}
            placeholder="Masukkan license key"
            autoCapitalize="characters"
            autoComplete="one-time-code"
            spellCheck={false}
          />
        </label>
        {message && <div className="notice error">{message}</div>}
        <button className="primary-button" disabled={!canSubmit}>
          {submitting ? "Memvalidasi..." : "Buka Sekarang"}
        </button>
      </form>

      <div className="license-help">
        <p>1 license key hanya untuk 1 device/browser. Jika ganti device atau hapus data browser, gunakan license baru.</p>
        <a className="access-link" href={accessLandingUrl} target="_blank" rel="noreferrer">
          Dapatkan Akses <ExternalLink size={17} />
        </a>
      </div>
    </section>
  );
}

function Onboarding({ onComplete }: { onComplete: (husbandName: string, wifeName: string) => void }) {
  const [step, setStep] = useState(0);
  const [halal, setHalal] = useState(false);
  const [consent, setConsent] = useState(false);
  const [husbandName, setHusbandName] = useState("");
  const [wifeName, setWifeName] = useState("");
  const [showInstall, setShowInstall] = useState(false);
  const validNames = husbandName.trim().length >= 2 && wifeName.trim().length >= 2;

  return (
    <section className="panel onboarding-panel">
      <LogoLockup />
      <div className="step-dots" aria-label={`Onboarding step ${step + 1} of 5`}>
        {[0, 1, 2, 3, 4].map((item) => (
          <span key={item} className={item === step ? "dot active" : "dot"} />
        ))}
      </div>

      {step === 0 && (
        <OnboardingStep
          title="Selamat datang di Intimacy Card Game"
          body="Game privat dari The Lovebird Club™ untuk pasangan yang ingin ngobrol, bermain, dan dekat lagi."
          button="Mulai"
          onNext={() => setStep(1)}
        />
      )}

      {step === 1 && (
        <div className="stack">
          <h1>Untuk pasangan halal/sah</h1>
          <p>Dengan melanjutkan, kami menyatakan game ini hanya digunakan oleh pasangan halal/sah.</p>
          <p>The Lovebird Club™ berlepas diri dari penggunaan di luar batas tersebut.</p>
          <label className="check-row">
            <input type="checkbox" checked={halal} onChange={(event) => setHalal(event.target.checked)} />
            <span>Saya menggunakan game ini bersama pasangan halal/sah saya.</span>
          </label>
          <button className="primary-button" disabled={!halal} onClick={() => setStep(2)}>
            Saya Mengerti <ChevronRight size={18} />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="stack">
          <h1>Consent & Comfort</h1>
          <p>Semua kartu boleh dilewati.</p>
          <p>Kalau salah satu tidak nyaman, tekan Skip.</p>
          <p>Tujuan game ini bukan memaksa, tapi membantu kalian lebih dekat.</p>
          <label className="check-row">
            <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
            <span>Kami sepakat untuk saling menghormati dan boleh skip kapan saja.</span>
          </label>
          <button className="primary-button" disabled={!consent} onClick={() => setStep(3)}>
            Lanjut <ChevronRight size={18} />
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="stack">
          <h1>Nama Pemain</h1>
          <label className="field">
            <span>Nama Suami</span>
            <input
              maxLength={24}
              minLength={2}
              value={husbandName}
              onChange={(event) => setHusbandName(event.target.value)}
              placeholder="Ketik Nama Suami"
            />
          </label>
          <label className="field">
            <span>Nama Istri</span>
            <input
              maxLength={24}
              minLength={2}
              value={wifeName}
              onChange={(event) => setWifeName(event.target.value)}
              placeholder="Ketik Nama Istri"
            />
          </label>
          <button className="primary-button" disabled={!validNames} onClick={() => setStep(4)}>
            Simpan Nama <Check size={18} />
          </button>
        </div>
      )}

      {step === 4 && (
        <div className="stack">
          <h1>Simpan dengan aman</h1>
          <p>Saran: tambahkan ke Home Screen dan simpan aplikasi ini dengan aman.</p>
          <p>Jangan dibuka anak-anak atau orang lain.</p>
          {showInstall && <InstallInstructions />}
          <button className="secondary-button" onClick={() => setShowInstall((value) => !value)}>
            Lihat Cara Add to Home Screen
          </button>
          <button className="primary-button" onClick={() => onComplete(husbandName.trim(), wifeName.trim())}>
            Lanjut ke Game <ChevronRight size={18} />
          </button>
        </div>
      )}
    </section>
  );
}

function OnboardingStep({
  title,
  body,
  button,
  onNext
}: {
  title: string;
  body: string;
  button: string;
  onNext: () => void;
}) {
  return (
    <div className="stack">
      <h1>{title}</h1>
      <p>{body}</p>
      <button className="primary-button" onClick={onNext}>
        {button} <ChevronRight size={18} />
      </button>
    </div>
  );
}

function HomeScreen({
  state,
  onStart,
  onContinue,
  onSettings
}: {
  state: AppState;
  onStart: (mode: GameMode) => void;
  onContinue: () => void;
  onSettings: () => void;
}) {
  return (
    <section className="home-screen">
      <header className="topbar">
        <LogoLockup />
        <div className="icon-actions">
          <a className="icon-button" href={brandUrl} target="_blank" rel="noreferrer" aria-label="Instagram The Lovebird Club">
            <Instagram size={20} />
          </a>
          <button className="icon-button" onClick={onSettings} aria-label="Settings">
            <Settings size={20} />
          </button>
        </div>
      </header>

      <div className="hero-copy">
        <p className="tagline">Play naughty. Get closer.</p>
        <h1>Intimacy Card Game</h1>
        <p>Halo, {state.husbandName} & {state.wifeName}</p>
      </div>

      {state.activeSession && (
        <button className="continue-card" onClick={onContinue}>
          <span>
            <strong>Lanjutkan sesi</strong>
            <small>
              {modeLabel(state.activeSession.mode, state.husbandName, state.wifeName)} ·{" "}
              {state.activeSession.totalCompleted} kartu selesai
            </small>
          </span>
          <ChevronRight size={20} />
        </button>
      )}

      <div className="mode-list">
        {modeOptions(state.husbandName, state.wifeName).map((mode) => (
          <button className="mode-card" key={mode.id} onClick={() => onStart(mode.id)}>
            <span className="mode-icon">
              <mode.icon size={22} />
            </span>
            <span>
              <strong>{mode.title}</strong>
              <small>{mode.description}</small>
              <em>Kalian boleh skip kartu apa pun. Mainkan hanya yang sama-sama nyaman.</em>
            </span>
          </button>
        ))}
      </div>

      <p className="source-note">
        {CARD_SOURCE.totalCards} kartu aktif · {CARD_SOURCE.talksCards} Talks · {CARD_SOURCE.foreplayHusbandCards} Foreplay by Suami ·{" "}
        {CARD_SOURCE.foreplayWifeCards} Foreplay by Istri · {CARD_SOURCE.penetrationCards} Penetrasi
      </p>
    </section>
  );
}

function GameScreen({
  session,
  card,
  husbandName,
  wifeName,
  cardRevealed,
  timerRemaining,
  timerRunning,
  onReveal,
  onStartTimer,
  onAddTime,
  onComplete,
  onSkip,
  onChangePenetrationCard,
  onEnd,
  onHide,
  onBackHome,
  onChoosePenetrationLevel,
  isBetweenCards
}: {
  session: GameSession;
  card?: GameCard;
  husbandName: string;
  wifeName: string;
  cardRevealed: boolean;
  timerRemaining: number;
  timerRunning: boolean;
  onReveal: () => void;
  onStartTimer: () => void;
  onAddTime: () => void;
  onComplete: () => void;
  onSkip: () => void;
  onChangePenetrationCard: () => void;
  onEnd: () => void;
  onHide: () => void;
  onBackHome: () => void;
  onChoosePenetrationLevel: (level: PenetrationLevel) => void;
  isBetweenCards: boolean;
}) {
  if (!card && session.currentDeck === "penetration" && !session.selectedPenetrationLevel && !isBetweenCards) {
    return (
      <section className="game-screen">
        <header className="topbar">
          <button className="icon-button" onClick={onBackHome} aria-label="Home">
            <Home size={20} />
          </button>
          <div className="game-mini">
            <strong>Penetrasi</strong>
            <small>{session.penetrationCompleted} selesai</small>
          </div>
          <div className="icon-actions">
            <button className="icon-button" onClick={onHide} aria-label="Hide">
              <EyeOff size={20} />
            </button>
          </div>
        </header>

        {session.pendingNotice && <div className="notice">{session.pendingNotice}</div>}

        <section className="panel level-picker">
          <p className="eyebrow">Pilih level ronde ini</p>
          <h1>Penetrasi</h1>
          <p>Setiap ronde kalian pilih level dulu, lalu app akan membuka satu kartu posisi secara random.</p>
          <div className="level-grid">
            {(["normal", "hard", "extreme"] as PenetrationLevel[]).map((level) => (
              <button className="level-button" key={level} onClick={() => onChoosePenetrationLevel(level)}>
                <strong>{levelLabel(level)}</strong>
                <small>{levelDescription(level)}</small>
              </button>
            ))}
          </div>
        </section>
      </section>
    );
  }

  if (!card && isBetweenCards) {
    return (
      <section className="panel transition-screen">
        <LogoLockup />
        <h1>Checkpoint sebentar.</h1>
        <p>Kartu berikutnya akan muncul setelah kalian memilih.</p>
      </section>
    );
  }

  if (!card) {
    return (
      <section className="panel">
        <LogoLockup />
        <h1>Maaf, kartu belum berhasil dimuat.</h1>
        <p>Coba refresh halaman atau mulai sesi baru dari Home.</p>
        <button className="primary-button" onClick={onBackHome}>
          <Home size={18} /> Home
        </button>
      </section>
    );
  }

  const heading = cardHeading(card, husbandName, wifeName);
  const subheading = cardSubheading(card, husbandName, wifeName);
  const personalizedText = renderPersonalizedText(card.text, husbandName, wifeName);

  return (
    <section className="game-screen">
      <header className="topbar">
        <button className="icon-button" onClick={onBackHome} aria-label="Home">
          <Home size={20} />
        </button>
        <div className="game-mini">
          <strong>{deckLabel(session.currentDeck, husbandName, wifeName)}</strong>
          <small>{session.totalCompleted} selesai</small>
        </div>
        <div className="icon-actions">
          <button className="icon-button" onClick={onHide} aria-label="Hide">
            <EyeOff size={20} />
          </button>
        </div>
      </header>

      {session.pendingNotice && <div className="notice">{session.pendingNotice}</div>}

      <div className={cardRevealed ? "deck-stage revealed" : "deck-stage"}>
        {!cardRevealed ? (
          <button className="deck-back-card" onClick={onReveal} aria-label="Open card">
            <span>Tap to open</span>
          </button>
        ) : (
          <article className="front-card-shell">
            <div className="front-card-content">
              <p className="eyebrow">{cardTypeLabel(card)}</p>
              <p className="turn-label">{subheading}</p>
              <h1>{heading}</h1>
              <p className="card-text">{personalizedText}</p>
              {card.durationLabel && (
                <div className="timer-box">
                  <div>
                    <span>Durasi</span>
                    <strong>{card.durationLabel}</strong>
                  </div>
                  <div className="timer-readout">
                    <Timer size={18} />
                    <strong>{formatTimer(timerRemaining)}</strong>
                  </div>
                  <div className="timer-actions">
                    <button className="small-button" onClick={onStartTimer} disabled={timerRunning || timerRemaining === 0}>
                      <Play size={15} /> Mulai Timer
                    </button>
                    <button className="small-button" onClick={onAddTime}>
                      Butuh Waktu Lagi
                    </button>
                  </div>
                </div>
              )}
            </div>
          </article>
        )}
      </div>

      {cardRevealed && (
        <nav className="bottom-controls" aria-label="Game controls">
          {card.deck === "penetration" ? (
            <button className="control-button secondary" onClick={onChangePenetrationCard}>
              <Shuffle size={19} />
              Ganti Kartu
            </button>
          ) : (
            <button className="control-button secondary" onClick={onSkip}>
              <SkipForward size={19} />
              Skip
            </button>
          )}
          <button className="control-button primary" onClick={onComplete}>
            <Check size={20} />
            {card.deck === "talks" ? "Selesai Dijawab" : card.deck === "penetration" ? "Selesai" : "Tandai Selesai"}
          </button>
          <button className="control-button quiet" onClick={onEnd}>
            Akhiri Game
          </button>
        </nav>
      )}
    </section>
  );
}

function SummaryScreen({
  summary,
  onHome,
  onNew
}: {
  summary?: SessionSummary;
  onHome: () => void;
  onNew: () => void;
}) {
  if (!summary) {
    return (
      <section className="panel">
        <LogoLockup />
        <h1>Belum ada ringkasan.</h1>
        <button className="primary-button" onClick={onHome}>
          <Home size={18} /> Home
        </button>
      </section>
    );
  }

  return (
    <section className="panel summary-screen">
      <LogoLockup />
      <h1>Sesi malam ini selesai.</h1>
      <p>{finalStatusLabel(summary.finalStatus)}. Kalian bisa main lagi kapan pun sudah siap.</p>
      <button className="primary-button" onClick={onNew}>
        Mulai Sesi Baru <RotateCcw size={18} />
      </button>
      <button className="secondary-button" onClick={onHome}>
        Main Lagi Nanti
      </button>
    </section>
  );
}

function SettingsScreen({
  state,
  onBack,
  onSaveNames,
  onResetOnboarding
}: {
  state: AppState;
  onBack: () => void;
  onSaveNames: (husbandName: string, wifeName: string) => void;
  onResetOnboarding: () => void;
}) {
  const [husbandName, setHusbandName] = useState(state.husbandName);
  const [wifeName, setWifeName] = useState(state.wifeName);
  const canSave = husbandName.trim().length >= 2 && wifeName.trim().length >= 2;

  return (
    <section className="panel settings-screen">
      <header className="settings-header">
        <LogoLockup />
        <button className="icon-button" onClick={onBack} aria-label="Close settings">
          <X size={20} />
        </button>
      </header>
      <h1>Settings</h1>
      <label className="field">
        <span>Nama Suami</span>
        <input maxLength={24} value={husbandName} onChange={(event) => setHusbandName(event.target.value)} />
      </label>
      <label className="field">
        <span>Nama Istri</span>
        <input maxLength={24} value={wifeName} onChange={(event) => setWifeName(event.target.value)} />
      </label>
      <button className="primary-button" disabled={!canSave} onClick={() => onSaveNames(husbandName.trim(), wifeName.trim())}>
        Simpan Nama <Check size={18} />
      </button>
      <div className="soft-box">
        <p>Simpan aplikasi ini dengan aman. Jangan dibuka anak-anak atau orang lain.</p>
        <InstallInstructions />
      </div>
      <button className="secondary-button" onClick={onResetOnboarding}>
        Reset Onboarding
      </button>
    </section>
  );
}

function TalksCheckpointModal({
  husbandName,
  wifeName,
  onAction
}: {
  husbandName: string;
  wifeName: string;
  onAction: (action: "continue" | "foreplay_husband" | "foreplay_wife" | "penetration") => void;
}) {
  const husband = husbandName || "Suami";
  const wife = wifeName || "Istri";
  return (
    <div className="modal-card">
      <h2>Masih mau lanjut ngobrol?</h2>
      <p>Kalian sudah menyelesaikan 6 kartu Talks. Mau lanjut ngobrol dulu, pindah Foreplay, atau langsung Penetrasi?</p>
      <button className="primary-button" onClick={() => onAction("continue")}>
        Lanjut Talks dulu
      </button>
      <button className="secondary-button" onClick={() => onAction("foreplay_husband")}>
        Lanjut ke Foreplay by {husband}
      </button>
      <button className="secondary-button" onClick={() => onAction("foreplay_wife")}>
        Lanjut ke Foreplay by {wife}
      </button>
      <button className="secondary-button" onClick={() => onAction("penetration")}>
        Langsung Penetrasi
      </button>
    </div>
  );
}

function ForeplayCheckpointModal({
  currentDeck,
  husbandName,
  wifeName,
  onAction
}: {
  currentDeck?: CardDeck;
  husbandName: string;
  wifeName: string;
  onAction: (action: "continue" | "switch" | "penetration") => void;
}) {
  const currentName = currentDeck === "foreplay_wife" ? wifeName || "Istri" : husbandName || "Suami";
  const nextName = currentDeck === "foreplay_wife" ? husbandName || "Suami" : wifeName || "Istri";
  return (
    <div className="modal-card">
      <h2>Masih tahan?</h2>
      <p>Kalian sudah menyelesaikan 6 kartu Foreplay. Mau lanjut performer yang sama, gantian, atau masuk Penetrasi?</p>
      <button className="primary-button" onClick={() => onAction("continue")}>
        Lanjut {currentName}
      </button>
      <button className="secondary-button" onClick={() => onAction("switch")}>
        Gantian ke {nextName}
      </button>
      <button className="secondary-button" onClick={() => onAction("penetration")}>
        Udah gak kuat, mau masuk aja
      </button>
    </div>
  );
}

function PenetrationCheckpointModal({ onContinue }: { onContinue: () => void }) {
  return (
    <div className="modal-card">
      <h2>Masih kuat lanjut?</h2>
      <p>Wow! Kalian luar biasa...</p>
      <button className="primary-button" onClick={onContinue}>
        Lanjut, pilih level lagi
      </button>
    </div>
  );
}

function PenetrationEndModal({ onAction }: { onAction: (action: "home" | "restart") => void }) {
  return (
    <div className="modal-card">
      <h2>Udah selesai? Gimana? Enak?</h2>
      <button className="primary-button" onClick={() => onAction("home")}>
        Enak banget, cape deh....
      </button>
      <button className="secondary-button" onClick={() => onAction("restart")}>
        Enak, jadi pgn lagi, gas!
      </button>
    </div>
  );
}

function EndConfirmModal({ onCancel, onEnd }: { onCancel: () => void; onEnd: () => void }) {
  return (
    <div className="modal-card">
      <h2>Akhiri sesi malam ini?</h2>
      <button className="danger-button" onClick={onEnd}>
        Ya, akhiri
      </button>
      <button className="secondary-button" onClick={onCancel}>
        Kembali main
      </button>
    </div>
  );
}

function ModalLayer({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  return (
    <div className="modal-layer" role="dialog" aria-modal="true">
      <button className="modal-scrim" aria-label="Close modal" onClick={onClose} />
      {children}
    </div>
  );
}

function SafeScreen({ onBack }: { onBack: () => void }) {
  return (
    <main className="safe-screen">
      <LogoLockup />
      <h1>Private by design.</h1>
      <button className="primary-button" onClick={onBack}>
        Kembali
      </button>
    </main>
  );
}

function LogoLockup() {
  return (
    <div className="logo-lockup">
      <img className="logo-mark-img" src="/img/logo-the-lovebird-club.png" alt="The Lovebird Club logo" />
      <span>
        <strong>Intimacy Card Game</strong>
        <small>
          by The Lovebird Club<sup>™</sup> · Play naughty. Get closer.
        </small>
      </span>
    </div>
  );
}

function InstallInstructions() {
  return (
    <div className="install-box">
      <p>iOS Safari: tap Share, lalu pilih Add to Home Screen.</p>
      <p>Android Chrome: tap Menu, lalu pilih Add to home screen atau Install app.</p>
    </div>
  );
}

function levelDescription(level: PenetrationLevel): string {
  if (level === "normal") return "Ronde nyaman dan pelan.";
  if (level === "hard") return "Ronde lebih intens.";
  return "Ronde paling menantang.";
}

function formatTimer(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}
